import { format, parseISO } from "date-fns";
import type { DateRange } from "react-day-picker";

import type { BranchOperationHours } from "@/lib/booking/branch-hours";
import { isTimeWithinOperationHours } from "@/lib/booking/branch-hours";
import { minutesToApiTime } from "@/lib/booking/time-slots";
import type { VehicleSearchPayload } from "@/lib/types/vehicle";

export type VehicleType = "car" | "delivery";

export type BookingSearchFormValues = {
  vehicle: VehicleType;
  pickupLocation: string;
  returnLocation: string;
  differentReturn: boolean;
  range?: DateRange;
  pickupTime?: number;
  returnTime?: number;
};

export type BookingSearchParams = {
  vehicle: VehicleType;
  pickup: string;
  return?: string;
  pickupDate: string;
  returnDate: string;
  pickupTime?: number;
  returnTime?: number;
};

const PARAM_VEHICLE = "vehicle";
const PARAM_PICKUP = "pickup";
const PARAM_RETURN = "return";
const PARAM_PICKUP_DATE = "pickupDate";
const PARAM_RETURN_DATE = "returnDate";
const PARAM_PICKUP_TIME = "pickupTime";
const PARAM_RETURN_TIME = "returnTime";

function isVehicleType(value: string): value is VehicleType {
  return value === "car" || value === "delivery";
}

export function bookingSearchParamsToQueryString(
  params: BookingSearchParams,
): string {
  const search = new URLSearchParams();
  search.set(PARAM_VEHICLE, params.vehicle);
  search.set(PARAM_PICKUP, params.pickup);
  if (params.return) {
    search.set(PARAM_RETURN, params.return);
  }
  search.set(PARAM_PICKUP_DATE, params.pickupDate);
  search.set(PARAM_RETURN_DATE, params.returnDate);
  if (params.pickupTime !== undefined) {
    search.set(PARAM_PICKUP_TIME, String(params.pickupTime));
  }
  if (params.returnTime !== undefined) {
    search.set(PARAM_RETURN_TIME, String(params.returnTime));
  }
  return search.toString();
}

export function buildBookingUrl(params: BookingSearchParams): string {
  const query = bookingSearchParamsToQueryString(params);
  return `/booking?${query}`;
}

export function parseBookingSearchParams(
  searchParams: URLSearchParams,
): BookingSearchParams | null {
  const vehicle = searchParams.get(PARAM_VEHICLE) ?? "";
  const pickup = searchParams.get(PARAM_PICKUP) ?? "";
  const pickupDate = searchParams.get(PARAM_PICKUP_DATE) ?? "";
  const returnDate = searchParams.get(PARAM_RETURN_DATE) ?? "";

  if (!isVehicleType(vehicle) || !pickup || !pickupDate || !returnDate) {
    return null;
  }

  const returnLocation = searchParams.get(PARAM_RETURN) ?? undefined;
  const pickupTimeRaw = searchParams.get(PARAM_PICKUP_TIME);
  const returnTimeRaw = searchParams.get(PARAM_RETURN_TIME);

  return {
    vehicle,
    pickup,
    return: returnLocation || undefined,
    pickupDate,
    returnDate,
    pickupTime: pickupTimeRaw ? Number(pickupTimeRaw) : undefined,
    returnTime: returnTimeRaw ? Number(returnTimeRaw) : undefined,
  };
}

export type BookingSearchValidationOptions = {
  pickupHours?: BranchOperationHours | null;
  returnHours?: BranchOperationHours | null;
};

export function formValuesToSearchParams(
  values: BookingSearchFormValues,
  options?: BookingSearchValidationOptions,
): BookingSearchParams | null {
  if (!isBookingSearchFormComplete(values, options)) {
    return null;
  }

  return {
    vehicle: values.vehicle,
    pickup: values.pickupLocation,
    return:
      values.differentReturn && values.returnLocation
        ? values.returnLocation
        : undefined,
    pickupDate: format(values.range.from, "yyyy-MM-dd"),
    returnDate: format(values.range.to, "yyyy-MM-dd"),
    pickupTime: values.pickupTime,
    returnTime: values.returnTime,
  };
}

export type CompleteBookingSearchFormValues = BookingSearchFormValues & {
  range: { from: Date; to: Date };
  pickupTime: number;
  returnTime: number;
};

export function isBookingSearchFormComplete(
  values: BookingSearchFormValues,
  options?: BookingSearchValidationOptions,
): values is CompleteBookingSearchFormValues {
  if (!values.pickupLocation) {
    return false;
  }

  if (values.differentReturn && !values.returnLocation) {
    return false;
  }

  if (!values.range?.from || !values.range?.to) {
    return false;
  }

  if (values.pickupTime === undefined || values.returnTime === undefined) {
    return false;
  }

  if (options) {
    if (!options.pickupHours || !options.returnHours) {
      return false;
    }

    if (
      !isTimeWithinOperationHours(values.pickupTime, options.pickupHours) ||
      !isTimeWithinOperationHours(values.returnTime, options.returnHours)
    ) {
      return false;
    }
  }

  return true;
}

export function searchParamsToFormValues(
  params: BookingSearchParams,
): BookingSearchFormValues {
  return {
    vehicle: params.vehicle,
    pickupLocation: params.pickup,
    returnLocation: params.return ?? "",
    differentReturn: Boolean(params.return),
    range: {
      from: parseISO(params.pickupDate),
      to: parseISO(params.returnDate),
    },
    pickupTime: params.pickupTime,
    returnTime: params.returnTime,
  };
}

export type VehicleSearchFilterPayload = {
  categoryIds?: number[];
  fuelTypeIds?: number[];
  transmissionIds?: number[];
};

const DEFAULT_API_TIME = "08:00";

export function bookingSearchParamsToVehiclePayload(
  params: BookingSearchParams,
  filters?: VehicleSearchFilterPayload,
): VehicleSearchPayload {
  return {
    pickup_location: Number(params.pickup),
    return_location: Number(params.return ?? params.pickup),
    from_date: params.pickupDate,
    from_time:
      params.pickupTime !== undefined
        ? minutesToApiTime(params.pickupTime)
        : DEFAULT_API_TIME,
    to_date: params.returnDate,
    to_time:
      params.returnTime !== undefined
        ? minutesToApiTime(params.returnTime)
        : DEFAULT_API_TIME,
    veh_categories: filters?.categoryIds ?? [],
    fuel_types: filters?.fuelTypeIds ?? [],
    transmission_types: filters?.transmissionIds ?? [],
  };
}

export function vehicleSearchPayloadToQueryParams(
  payload: VehicleSearchPayload,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    pickup_location: payload.pickup_location,
    return_location: payload.return_location,
    from_date: payload.from_date,
    from_time: payload.from_time,
    to_date: payload.to_date,
    to_time: payload.to_time,
  };

  if (payload.veh_categories.length > 0) {
    params.veh_categories = payload.veh_categories.join(",");
  }

  if (payload.fuel_types.length > 0) {
    params.fuel_types = payload.fuel_types.join(",");
  }

  if (payload.transmission_types.length > 0) {
    params.transmission_types = payload.transmission_types.join(",");
  }

  return params;
}

export function appendSearchToPath(
  path: string,
  searchParams: URLSearchParams | string,
): string {
  const query =
    typeof searchParams === "string"
      ? searchParams
      : searchParams.toString();
  if (!query) {
    return path;
  }
  return `${path}?${query}`;
}
